import {useAlertCallback} from '@jobseeker/lotus/hooks';
import {useEffect, useState} from 'react';
import {useFormik} from 'formik';
import {IImjobsDoneIcon, EmailCaptureIcon} from '@jobseeker/lotus/assets/icons';
import * as yup from 'yup';
import {
  Box,
  FormControl,
  FormHelperText as MuiFormHelperText,
  styled,
  Typography,
} from '@mui/material';
import Image from 'next/image';
import Button from '@jobseeker/lotus/elements/ButtonV2';
import {Input} from '@jobseeker/lotus/elements';
import {VersionVariants} from '@jobseeker/lotus/enums';
import {TrackEventUBA} from '@jobseeker/common-core/utils';

interface LinkedInEmailCaptureProps {
  jobId: string;
  trackJobfeedInterest: (email: string, sessionId: string) => Promise<void>;
}

interface EmailFormProps {
  isEmailSubmitted: boolean;
  setIsEmailSubmitted: (value: boolean) => void;
  onSuccess: () => void;
  trackJobfeedInterest: (email: string, sessionId: string) => Promise<void>;
  handleUbaTracking: (
    pageName: string,
    widgetType: string,
    action?: string,
    typeClick?: string,
    email?: string
  ) => void;
  capturedEmail: string;
  setCapturedEmail: (email: string) => void;
}

interface DialogHeaderProps {
  title: string;
  subtitile: string;
  icon: string;
}

const validationSchemaEmail = yup.object({
  email: yup
    .string()
    .email('Please enter a valid email address')
    .matches(
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
      'Please enter a valid email address'
    )
    .required('Please enter your email address'),
});

const FormHelperText = styled(MuiFormHelperText)(() => ({
  position: 'absolute',
}));

const DialogHeader = ({title, subtitile, icon}: DialogHeaderProps) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <Image src={icon} width={56} height={56} alt="done" />
      <Typography
        variant="subtitle1"
        color="text.primary"
        textAlign={'center'}
        fontWeight={600}
        mt={'14px'}
        mb={'7px'}
      >
        {title}
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          textAlign: 'center',
          mb: '17px',
        }}
      >
        {subtitile}
      </Typography>
    </Box>
  );
};

const EmailForm = ({
  isEmailSubmitted,
  setIsEmailSubmitted,
  onSuccess,
  trackJobfeedInterest,
  handleUbaTracking,
  capturedEmail,
  setCapturedEmail,
}: EmailFormProps) => {
  const formik = useFormik({
    initialValues: {
      email: '',
    },
    validationSchema: validationSchemaEmail,
    onSubmit: () => {},
  });

  const onEmailSubmit = async () => {
    handleUbaTracking(
      'LinkedIn User Acquasition Collect Email Popup',
      'Collect Email Popup',
      'click',
      'Get Job Updates',
      formik.values.email
    );
    formik.setFieldTouched('email', true, true);
    const errors = await formik.validateForm();

    if (errors?.email) {
      return;
    }

    try {
      await trackJobfeedInterest(formik.values.email, '');
      setCapturedEmail(formik.values.email);
      setIsEmailSubmitted(true);
      handleUbaTracking(
        'LinkedIn User Acquasition Collect Email Success',
        'Collect Email Success Popup',
        'view',
        '',
        formik.values.email
      );
    } catch (error) {
      console.error('Error tracking jobfeed interest:', error);
    }
  };

  const handleDoneClick = () => {
    handleUbaTracking(
      'LinkedIn User Acquasition Collect Email Success',
      'Collect Email Success Popup',
      'click',
      'Done',
      capturedEmail
    );
    onSuccess();
  };

  return !isEmailSubmitted ? (
    <Box>
      <DialogHeader
        title={'Get more curated jobs like this in your inbox'}
        subtitile={
          'Discover handpicked openings that match your skills and experience.'
        }
        icon={EmailCaptureIcon}
      />
      <Box>
        <FormControl
          sx={{
            width: '100%',
            pb: 1.5,
            display: isEmailSubmitted ? 'none' : 'block',
          }}
          error={formik.touched.email && Boolean(formik.errors.email)}
        >
          <Box
            sx={{
              display: 'flex',
              border: '1px solid #7E7E7E',
              borderRadius: '100px',
              ':hover, &.Mui-focused': {
                borderColor: '#7E7E7E',
              },
            }}
          >
            <Box sx={{flexGrow: 1}}>
              <Input
                variant={VersionVariants.VERSION_THREE}
                type={'email'}
                name="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                // error={formik.touched.email && Boolean(formik.errors.email)}
                placeholder="Enter your email"
                InputProps={{autoComplete: 'email'}}
                sx={{
                  '.MuiInputBase-root': {
                    '& fieldset': {
                      borderColor: 'transparent',
                    },
                    ':hover fieldset, &.Mui-focused fieldset': {
                      border: 'none',
                    },
                    mt: -1,
                    height: '100%',
                    minHeight: '50px',
                  },
                }}
              />
            </Box>
            <Box sx={{p: '6px'}}>
              <Button
                type="button"
                color="secondary"
                variant="contained"
                sx={{height: '100%', minWidth: '135px', fontSize: '12px'}}
                onClick={onEmailSubmit}
              >
                Get Job Updates
              </Button>
            </Box>
          </Box>
          {formik.touched.email && Boolean(formik.errors.email) && (
            <Box display={'flex'}>
              <FormHelperText>
                <>{formik?.errors?.email || ''}</>
              </FormHelperText>
            </Box>
          )}
        </FormControl>
      </Box>
      <Typography
        variant="caption"
        sx={{
          color: '#7E7E7E',
          mx: '14px',
        }}
      >
        No spam, Only relevant roles
      </Typography>
    </Box>
  ) : (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <DialogHeader
        title={"You're are all set!"}
        subtitile={
          'We’ll send you curated jobs similar to this straight to your inbox.'
        }
        icon={IImjobsDoneIcon}
      />
      <Button color="secondary" variant="contained" onClick={handleDoneClick}>
        Done
      </Button>
    </Box>
  );
};

export const LinkedInEmailCaptureComponent = ({
  jobId,
  trackJobfeedInterest,
}: LinkedInEmailCaptureProps) => {
  const [isEmailSubmitted, setIsEmailSubmitted] = useState(false);
  const [capturedEmail, setCapturedEmail] = useState<string>('');

  const handleUbaTracking = (
    pageName: string,
    widgetType: string,
    action = 'view',
    typeClick = 'CTA',
    email?: string
  ) => {
    const trackingData = {
      pageName: pageName,
      widgetName: pageName,
      widgetType: widgetType,
      sessionUsername: sessionStorage.getItem('userId') || '',
      ...(action === 'click' && {typeClick: typeClick}),
      jobID: jobId,
      ...(email && {emailID: email}),
    };
    TrackEventUBA(
      action === 'click' ? 'leadCapturewidgetClick' : 'leadCapturewidgetView',
      action,
      trackingData
    );
  };

  const handleClose = () => {
    if (isEmailSubmitted) {
      handleUbaTracking(
        'LinkedIn User Acquasition Collect Email Success',
        'Collect Email Success Popup',
        'click',
        'Close',
        capturedEmail
      );
    } else {
      handleUbaTracking(
        'LinkedIn User Acquasition Collect Email Popup',
        'Collect Email Popup',
        'click',
        'Close',
        capturedEmail
      );
    }
    toggleLinkedInEmailCaptureDialog();
  };

  const [LinkedInEmailCaptureDialog, toggleLinkedInEmailCaptureDialog] =
    useAlertCallback({
      BodyContent: () => (
        <EmailForm
          isEmailSubmitted={isEmailSubmitted}
          setIsEmailSubmitted={setIsEmailSubmitted}
          onSuccess={toggleLinkedInEmailCaptureDialog}
          trackJobfeedInterest={trackJobfeedInterest}
          handleUbaTracking={handleUbaTracking}
          capturedEmail={capturedEmail}
          setCapturedEmail={setCapturedEmail}
        />
      ),
      primaryCallback: console.log,
      onClose: handleClose,
    });

  useEffect(() => {
    toggleLinkedInEmailCaptureDialog();
    handleUbaTracking(
      'LinkedIn User Acquasition Collect Email Popup',
      'Collect Email Popup',
      'view'
    );
  }, []);

  return (
    <>
      <LinkedInEmailCaptureDialog />
    </>
  );
};
